import React, { useCallback, useEffect, useMemo, useState } from 'react'
import StatusPill from '../components/StatusPill'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/AuthContext'
import {
  bookingComponentDraft,
  bookingJobSummary,
  fmtDate,
  generateCustomerReport,
  isGeneralJob,
  isSuspensionJob,
  JOB_FINANCIAL_SELECT,
  jobFinancials,
  jobHasApprovedQuote,
  jobModuleLabel,
  jobPaymentMeta,
  jobProgressMeta,
  jobServiceScopeSummary,
  jobStatusMeta,
  normaliseJobStatus,
} from '../lib/utils'
import { generalServices, suspensionServices } from '../lib/serviceMenu'
import { technicianForJob, technicianName } from '../lib/capacityPlanning'
import ComponentModal from '../components/ComponentModal'
import JobModal from '../components/JobModal'
import NewJobIntakeModal from '../components/NewJobIntakeModal'
import ViewJobModal from '../components/ViewJobModal'

const ACTIVE_STATUSES = new Set([
  'awaiting_customer_item',
  'received',
  'estimate_sent',
  'awaiting_parts',
  'ready_for_collection',
])

export default function TabletMode() {
  const { workshopId, workshop, profile } = useAuth()
  const [customers, setCustomers] = useState([])
  const [bikes, setBikes] = useState([])
  const [components, setComponents] = useState([])
  const [jobs, setJobs] = useState([])
  const [team, setTeam] = useState([])
  const [services, setServices] = useState([])
  const [generalJobServices, setGeneralJobServices] = useState([])
  const [loading, setLoading] = useState(true)
  const [hasLoaded, setHasLoaded] = useState(false)
  const [loadError, setLoadError] = useState('')
  const [mode, setMode] = useState('today')
  const [techFilter, setTechFilter] = useState('all')
  const [showJobModal, setShowJobModal] = useState(false)
  const [editJob, setEditJob] = useState(null)
  const [viewJobId, setViewJobId] = useState(null)
  const [compModal, setCompModal] = useState(null)
  const [componentLinkError, setComponentLinkError] = useState(null)

  const load = useCallback(async () => {
    if (!workshopId) return
    setLoadError('')
    const [c, b, co, j, s, teamRes] = await Promise.all([
      supabase.from('customers').select('*').eq('workshop_id', workshopId),
      supabase.from('bikes').select('*').eq('workshop_id', workshopId),
      supabase.from('components').select('*').eq('workshop_id', workshopId),
      supabase.from('jobs').select(JOB_FINANCIAL_SELECT).eq('workshop_id', workshopId).order('date', { ascending: true }),
      supabase.from('services').select('*').eq('workshop_id', workshopId).order('sort_order'),
      supabase.from('profiles').select('id, full_name, role').eq('workshop_id', workshopId).order('full_name'),
    ])
    const readError = [c, b, co, j, s, teamRes].find(result => result?.error)?.error
    if (readError) {
      setLoadError(readError.message || 'Could not load Bench mode.')
      setLoading(false)
      return
    }
    setCustomers(c.data || [])
    setBikes(b.data || [])
    setComponents(co.data || [])
    setJobs(j.data || [])
    setTeam(teamRes.data || [])
    const serviceRows = s.data || []
    setServices(suspensionServices(serviceRows))
    setGeneralJobServices(generalServices(serviceRows))
    setHasLoaded(true)
    setLoading(false)
  }, [workshopId])

  useEffect(() => { load() }, [load])

  const todayStr = new Date().toISOString().slice(0, 10)
  const technicianOptions = useMemo(() => {
    const memberRows = team.map(member => ({ value: member.id, label: technicianName(member) }))
    const memberNames = new Set(memberRows.map(row => row.label.toLowerCase()))
    const legacyRows = [...new Set(jobs.map(j => String(j.technician || '').trim()).filter(Boolean))]
      .filter(name => !memberNames.has(name.toLowerCase()))
      .sort()
      .map(name => ({ value: `name:${name}`, label: name }))
    return [...memberRows, ...legacyRows]
  }, [jobs, team])
  const profileName = String(profile?.full_name || '').trim()

  function getCustomer(job) {
    return customers.find(c => c.id === job.customer_id)
  }

  function getComponent(job) {
    return components.find(c => c.id === job.component_id)
  }

  function getBike(job) {
    return bikes.find(b => b.id === job.bike_id)
  }

  function statusKey(job) {
    return normaliseJobStatus(job.status)
  }

  function isActive(job) {
    return ACTIVE_STATUSES.has(statusKey(job))
  }

  function addDays(dateStr, amount) {
    const d = new Date(`${dateStr}T12:00:00`)
    d.setDate(d.getDate() + amount)
    return d.toISOString().slice(0, 10)
  }

  function dueDate(job) {
    return job.due_date || job.date || ''
  }

  function jobTitle(job) {
    const comp = getComponent(job)
    if (comp) return [comp.year, comp.model].filter(Boolean).join(' ') || 'Component job'
    if (isGeneralJob(job)) return 'General Workshop job'
    return bookingJobSummary(job) || 'Booking job'
  }

  function jobSubtitle(job) {
    const cust = getCustomer(job)
    const comp = getComponent(job)
    return [
      cust ? `${cust.first_name} ${cust.last_name}` : 'Unknown customer',
      comp?.serial ? `Serial: ${comp.serial}` : null,
      assignedTechnicianName(job) ? `Tech: ${assignedTechnicianName(job)}` : 'No technician',
    ].filter(Boolean).join(' - ')
  }

  function serviceLine(job) {
    return jobServiceScopeSummary(job) || bookingJobSummary(job) || 'Service job'
  }

  function progressFor(job, key) {
    return jobProgressMeta(job).find(item => item.key === key)
  }

  function assignedTechnicianName(job) {
    const member = technicianForJob(job, team)
    return member ? technicianName(member) : String(job.technician || '').trim()
  }

  const activeJobs = jobs.filter(isActive)
  const tabletQueues = useMemo(() => {
    const todayJobs = activeJobs.filter(j => j.date === todayStr)
    const blockedJobs = activeJobs.filter(j => {
      const status = statusKey(j)
      return (isSuspensionJob(j) && !j.component_id) || status === 'awaiting_customer_item' || status === 'awaiting_parts' || (status === 'estimate_sent' && !jobHasApprovedQuote(j))
    })
    const workJobs = activeJobs.filter(j => statusKey(j) === 'received' || jobHasApprovedQuote(j))
    const qcJobs = activeJobs.filter(j => {
      const qc = progressFor(j, 'qc')
      return ['Started', 'Ready to sign', 'Failed'].includes(qc?.value) || (j.qc_signed_off_at && !j.report_sent_at)
    })
    const handoverJobs = activeJobs.filter(j => statusKey(j) === 'ready_for_collection' || jobPaymentMeta(j).key === 'requested')
    const upcomingJobs = activeJobs.filter(j => dueDate(j) >= todayStr && dueDate(j) <= addDays(todayStr, 7))
    return {
      today: todayJobs,
      active: activeJobs,
      blocked: blockedJobs,
      work: uniqueJobs([...workJobs, ...qcJobs]),
      handover: handoverJobs,
      upcoming: upcomingJobs,
      mine: profileName ? activeJobs.filter(j => (
        String(j.technician_id || '') === String(profile?.id || '')
        || assignedTechnicianName(j).toLowerCase() === profileName.toLowerCase()
      )) : [],
    }
  }, [activeJobs, profile?.id, profileName, team, todayStr])

  const modeButtons = [
    ['today', 'Today', tabletQueues.today.length],
    ['active', 'Active', tabletQueues.active.length],
    ['blocked', 'Blocked', tabletQueues.blocked.length],
    ['work', 'Service / QC', tabletQueues.work.length],
    ['handover', 'Handover', tabletQueues.handover.length],
    ['upcoming', 'Next 7 Days', tabletQueues.upcoming.length],
  ]
  if (profileName) modeButtons.splice(2, 0, ['mine', 'Mine', tabletQueues.mine.length])

  const filteredJobs = (tabletQueues[mode] || [])
    .filter(job => {
      if (techFilter === 'all') return true
      const member = technicianForJob(job, team)
      if (techFilter === 'unassigned') return !member && !String(job.technician || '').trim()
      if (techFilter.startsWith('name:')) return String(job.technician || '').trim() === techFilter.slice(5)
      return String(member?.id || job.technician_id || '') === techFilter
    })
    .sort((a, b) => {
      const aDate = dueDate(a) || '9999-12-31'
      const bDate = dueDate(b) || '9999-12-31'
      if (aDate !== bDate) return aDate.localeCompare(bDate)
      return (a.start_time || '99:99').localeCompare(b.start_time || '99:99')
    })

  const viewJob = jobs.find(j => j.id === viewJobId)
  const viewComp = viewJob ? getComponent(viewJob) : null
  const viewCust = viewJob ? getCustomer(viewJob) : null
  const viewBike = viewJob ? getBike(viewJob) : null

  function createComponentFromJob(job) {
    if (!isSuspensionJob(job)) {
      setComponentLinkError('General Workshop jobs do not use the suspension component workflow yet.')
      return
    }
    setComponentLinkError(null)
    setCompModal({
      customerId: job.customer_id,
      jobId: job.id,
      prefillData: bookingComponentDraft(job),
    })
  }

  async function handleComponentSaved(component) {
    const jobId = compModal?.jobId
    setComponentLinkError(null)
    if (jobId && component?.id) {
      const { error } = await supabase
        .from('jobs')
        .update({ component_id: component.id })
        .eq('id', jobId)
      if (error) setComponentLinkError(error.message)
    }
    setCompModal(null)
    await load()
  }

  function jobCard(job) {
    const customer = getCustomer(job)
    const component = getComponent(job)
    const generalJob = isGeneralJob(job)
    const status = jobStatusMeta(job)
    const financials = jobFinancials(job)
    const payment = jobPaymentMeta(job)
    const progress = jobProgressMeta(job)
    const overdue = dueDate(job) && dueDate(job) < todayStr
    return (
      <section key={job.id} className={`bench-refresh-card${overdue ? ' is-overdue' : ''}`}>
        <div className="bench-refresh-card-head">
          <div style={{ minWidth: 0 }}>
            <span className="refresh-kicker">{jobModuleLabel(job)} · {job.job_number || job.id}</span>
            <h2>{jobTitle(job)}</h2>
          </div>
          <StatusPill tone={status.tone}>{status.label}</StatusPill>
        </div>

        <p className="bench-refresh-subtitle">{jobSubtitle(job)} · {serviceLine(job)}</p>

        <div className="bench-refresh-meta">
          <TabletStat label="Date" value={dueDate(job) ? fmtDate(dueDate(job)) : '-'} tone={overdue ? 'red' : 'muted'} />
          <TabletStat label="Time" value={[job.start_time, job.finish_time].filter(Boolean).join(' - ') || '-'} />
          <TabletStat label="Customer" value={customer ? `${customer.first_name} ${customer.last_name}` : '-'} />
          <TabletStat label="Value" value={financials.totalCost ? `GBP ${financials.totalCost.toFixed(0)}` : '-'} tone="green" />
        </div>

        <div className="bench-refresh-badges">
          {generalJob && <span className="job-status-chip job-status-blue">{jobModuleLabel(job)}</span>}
          {jobHasApprovedQuote(job) && <StatusPill tone="green">Quote accepted</StatusPill>}
          {progress.map(item => (
            <StatusPill key={item.key} tone={item.tone}>{item.label}: {item.value}</StatusPill>
          ))}
          {payment.key !== 'not_requested' && <StatusPill tone={payment.tone}>Payment: {payment.label}</StatusPill>}
          {!generalJob && !component && <StatusPill tone="orange">Needs component</StatusPill>}
        </div>

        <div className="bench-refresh-actions">
          <button className="btn btn-primary" style={tabletButtonStyle} onClick={() => setEditJob(job)}>Open job</button>
          <button className="btn btn-ghost" style={tabletButtonStyle} onClick={() => setViewJobId(job.id)}>View</button>
          {!generalJob && !component && customer && (
            <button className="btn btn-green" style={tabletButtonStyle} onClick={() => createComponentFromJob(job)}>Component</button>
          )}
          {!generalJob && component && (
            <button className="btn btn-green" style={tabletButtonStyle} onClick={() => generateCustomerReport(job, customer, component, workshop)}>Report</button>
          )}
        </div>
      </section>
    )
  }

  if (loading) return <div className="empty"><span className="spinner" style={{ width: 24, height: 24 }} /></div>
  if (loadError && !hasLoaded) return (
    <div className="bench-refresh-page">
      <div className="page-head">
        <div>
          <div className="refresh-kicker">{workshop?.name || 'Workshop'}</div>
          <div className="page-title">Bench mode</div>
        </div>
      </div>
      <div className="alert alert-error bench-refresh-load-error" role="alert">
        <span>{loadError}</span>
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setLoading(true); load() }}>Try again</button>
      </div>
    </div>
  )

  return (
    <div className="bench-refresh-page">
      <div className="page-head bench-refresh-head">
        <div>
          <div className="refresh-kicker">{workshop?.name || 'Workshop'}</div>
          <div className="page-title">Bench mode</div>
          <div className="text-muted fs12 mt4">Real workshop queues and the same job safeguards, with larger touch targets.</div>
        </div>
        <div className="page-actions">
          <button className="btn btn-ghost" style={tabletButtonStyle} onClick={load}>Refresh</button>
          <button className="btn btn-primary" style={tabletButtonStyle} onClick={() => setShowJobModal(true)}>New Job</button>
        </div>
      </div>

      {loadError && (
        <div className="alert alert-error bench-refresh-load-error" role="alert">
          <span>{loadError} The open job and unsaved entries have been kept.</span>
          <button type="button" className="btn btn-ghost btn-sm" onClick={load}>Try again</button>
        </div>
      )}

      {componentLinkError && <div className="alert alert-error">{componentLinkError}</div>}

      <div className="bench-refresh-filters">
        <div className="bench-refresh-queues" role="group" aria-label="Bench queue">
          {modeButtons.map(([key, label, count]) => (
            <button
              key={key}
              className={`btn ${mode === key ? 'btn-primary' : 'btn-ghost'}`}
              aria-pressed={mode === key}
              style={{ ...tabletButtonStyle, justifyContent: 'space-between' }}
              onClick={() => setMode(key)}
            >
              <span>{label}</span>
              <strong>{count}</strong>
            </button>
          ))}
        </div>
        <div className="bench-refresh-tech-filter">
          <div className="field" style={{ marginBottom: 0 }}>
            <label>Technician</label>
            <select value={techFilter} onChange={e => setTechFilter(e.target.value)} style={{ minHeight: 44, fontSize: 15 }}>
              <option value="all">All technicians</option>
              <option value="unassigned">Unassigned</option>
              {technicianOptions.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
          </div>
          <div className="text-muted fs12" style={{ paddingBottom: 10 }}>
            Showing <span className="text-white fw7">{filteredJobs.length}</span> job{filteredJobs.length !== 1 ? 's' : ''}
          </div>
        </div>
      </div>

      <div className="bench-refresh-grid">
        {filteredJobs.length ? filteredJobs.map(jobCard) : (
          <div className="muted-empty">No jobs in this tablet queue.</div>
        )}
      </div>

      {showJobModal && (
        <NewJobIntakeModal
          userId={profile?.id}
          workshopId={workshopId}
          customers={customers}
          bikes={bikes}
          components={components}
          services={services}
          onClose={() => setShowJobModal(false)}
          onSaved={(job) => { setShowJobModal(false); if (job?.id) setViewJobId(job.id); load() }}
        />
      )}

      {compModal && (
        <ComponentModal
          workshopId={workshopId}
          customerId={compModal.customerId}
          prefillData={compModal.prefillData}
          onClose={() => setCompModal(null)}
          onSaved={handleComponentSaved}
          onDeleted={() => { setCompModal(null); load() }}
        />
      )}

      {editJob && (
        <JobModal
          workshopId={workshopId}
          customers={customers}
          bikes={bikes}
          components={components}
          services={services}
          generalServices={generalJobServices}
          initialData={editJob}
          returnLabel="Back to Bench mode"
          onClose={() => setEditJob(null)}
          onChanged={load}
          onSaved={savedJob => { setEditJob(current => current?.id === savedJob?.id ? { ...current, ...savedJob } : current); load() }}
          onDeleted={() => { setEditJob(null); load() }}
        />
      )}

      {viewJobId && viewJob && (
        <ViewJobModal
          job={viewJob}
          customer={viewCust}
          component={viewComp}
          bike={viewBike}
          workshop={workshop}
          services={services}
          generalServices={generalJobServices}
          onClose={() => setViewJobId(null)}
          onEdit={() => { setViewJobId(null); setEditJob(viewJob) }}
          onChanged={load}
          returnLabel="Back to Bench mode"
          onCreateComponent={isSuspensionJob(viewJob) && !viewComp && viewCust ? () => { setViewJobId(null); createComponentFromJob(viewJob) } : null}
        />
      )}
    </div>
  )
}

function TabletStat({ label, value, tone = 'muted' }) {
  return (
    <div style={{
      background: 'var(--panel)',
      border: '1px solid var(--border)',
      borderRadius: 6,
      padding: '9px 11px',
      minWidth: 0,
    }}>
      <div className="fs11 text-muted" style={{ textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 800 }}>{label}</div>
      <div className={tone === 'green' ? 'text-green fw7' : tone === 'red' ? 'text-red fw7' : tone === 'orange' ? 'text-orange fw7' : 'text-white fw7'} style={{ marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {value}
      </div>
    </div>
  )
}

function uniqueJobs(rows) {
  const seen = new Set()
  return rows.filter(job => {
    if (!job || seen.has(job.id)) return false
    seen.add(job.id)
    return true
  })
}

const tabletButtonStyle = {
  minHeight: 44,
  justifyContent: 'center',
  fontSize: 14,
  paddingLeft: 14,
  paddingRight: 14,
}
